import "./MetadocProcessingToaster.scss";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildMetadocPath } from "pages/metadoc/utils/metadocRoutes";
import type { ProcessingMetadoc } from "reducers/metadoc";
import {
  removeProcessingMetadoc,
  updateProcessingMetadocStatus,
} from "reducers/metadoc";
import { api } from "services/api";
import { useGetMetadocStatusQuery } from "services/metadoc-api";
import type { RootState } from "services/store";
import { useDispatch, useSelector } from "utils/redux";

type SingleToasterProps = {
  processingMetadoc: ProcessingMetadoc;
  index: number;
};

const SingleMetadocToaster = ({
  processingMetadoc,
  index,
}: SingleToasterProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isMinimized, setIsMinimized] = useState(false);

  // poll status (skip if metadocId is negative which means temporary/creating)
  const { data: metadocStatus } = useGetMetadocStatusQuery(
    { metadocId: processingMetadoc.metadocId },
    {
      skip: processingMetadoc.metadocId < 0,
      pollingInterval:
        processingMetadoc.metadocId > 0 &&
        processingMetadoc.status !== "completed" &&
        processingMetadoc.status !== "failed"
          ? 3000
          : 0,
    },
  );

  // update status from polling
  useEffect(() => {
    if (!metadocStatus) return;

    if (metadocStatus.status !== processingMetadoc.status) {
      dispatch(
        updateProcessingMetadocStatus({
          metadocId: processingMetadoc.metadocId,
          status: metadocStatus.status as ProcessingMetadoc["status"],
          errorMessage: metadocStatus.error_message || undefined,
        }),
      );

      // invalidate metadocs cache when status changes to completed
      // this ensures the metadoc table refetches and shows the updated status
      if (metadocStatus.status === "completed") {
        dispatch(api.util.invalidateTags(["Metadocs"]));
      }
    }
  }, [metadocStatus, processingMetadoc, dispatch]);

  // auto-dismiss after 5 seconds on successful completion
  useEffect(() => {
    if (processingMetadoc.status === "completed") {
      const timer = setTimeout(() => {
        dispatch(removeProcessingMetadoc(processingMetadoc.metadocId));
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [processingMetadoc.status, processingMetadoc.metadocId, dispatch]);

  const handleNavigate = () => {
    // only navigate if we have a real metadoc ID
    if (processingMetadoc.metadocId > 0) {
      navigate(
        buildMetadocPath(
          processingMetadoc.navigationContext ?? {
            workspace: "operations",
            fileId: processingMetadoc.fileId,
          },
          processingMetadoc.metadocId,
        ),
      );

      // dismiss toast after navigating (if completed)
      if (processingMetadoc.status === "completed") {
        dispatch(removeProcessingMetadoc(processingMetadoc.metadocId));
      }
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(removeProcessingMetadoc(processingMetadoc.metadocId));
  };

  const getIcon = () => {
    if (processingMetadoc.status === "completed") return "check_circle";
    if (processingMetadoc.status === "failed") return "error";
    return "sync";
  };

  const getStatusText = () => {
    if (processingMetadoc.status === "completed") return "Complete";
    if (processingMetadoc.status === "failed") return "Failed";
    if (processingMetadoc.metadocId < 0) return "Creating...";
    if (processingMetadoc.status === "pending") return "Queued";
    return "Processing";
  };

  const bottomPosition = 20 + index * 85; // stack toasters vertically

  if (isMinimized) {
    return (
      <div
        className={`MetadocProcessingToaster MetadocProcessingToaster--minimized MetadocProcessingToaster--${processingMetadoc.status}`}
        onClick={handleNavigate}
        style={{ bottom: `${bottomPosition}px` }}
      >
        <div className="metadoc-minimized-content">
          <span
            className={`material-icons ${processingMetadoc.status === "processing" || processingMetadoc.status === "pending" ? "spinning" : ""}`}
          >
            {getIcon()}
          </span>
          <span className="metadoc-minimized-text">{getStatusText()}</span>
          <button
            className="metadoc-action-button"
            onClick={(e) => {
              e.stopPropagation();
              setIsMinimized(false);
            }}
            aria-label="Expand"
          >
            <span className="material-icons">expand_less</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`MetadocProcessingToaster MetadocProcessingToaster--${processingMetadoc.status}`}
      onClick={handleNavigate}
      style={{ bottom: `${bottomPosition}px` }}
    >
      <div className="metadoc-header">
        <div className="metadoc-header-left">
          <span
            className={`material-icons metadoc-icon ${processingMetadoc.status === "processing" || processingMetadoc.status === "pending" ? "spinning" : ""}`}
          >
            {getIcon()}
          </span>
          <div className="metadoc-info">
            <div className="metadoc-name" title={processingMetadoc.name}>
              {processingMetadoc.name}
            </div>
            {processingMetadoc.status === "failed" &&
              processingMetadoc.errorMessage && (
                <div className="metadoc-error-message">
                  {processingMetadoc.errorMessage}
                </div>
              )}
          </div>
        </div>
        <div className="metadoc-header-actions">
          <button
            className="metadoc-action-button"
            onClick={(e) => {
              e.stopPropagation();
              setIsMinimized(true);
            }}
            aria-label="Minimize"
          >
            <span className="material-icons">minimize</span>
          </button>
          {(processingMetadoc.status === "completed" ||
            processingMetadoc.status === "failed") && (
            <button
              className="metadoc-action-button"
              onClick={handleDismiss}
              aria-label="Dismiss"
            >
              <span className="material-icons">close</span>
            </button>
          )}
        </div>
      </div>

      {(processingMetadoc.status === "processing" ||
        processingMetadoc.status === "pending" ||
        processingMetadoc.metadocId < 0) && (
        <div className="metadoc-status-text">
          {processingMetadoc.metadocId < 0
            ? "Creating Meta Document..."
            : processingMetadoc.status === "pending"
              ? "Queued for processing..."
              : "Pre-processing Documents..."}
        </div>
      )}

      {processingMetadoc.status === "completed" && (
        <div className="metadoc-complete-message">
          <span className="material-icons">open_in_new</span>
          <span>Click to view Meta Document</span>
        </div>
      )}
    </div>
  );
};

// Container component that renders all processing metadocs
const MetadocProcessingToaster = () => {
  const processingMetadocs = useSelector(
    (state: RootState) => state.metadoc.processingMetadocs,
  );

  return (
    <>
      {processingMetadocs.map((metadoc, index) => (
        <SingleMetadocToaster
          key={metadoc.metadocId}
          processingMetadoc={metadoc}
          index={index}
        />
      ))}
    </>
  );
};

export default MetadocProcessingToaster;
